import Phaser from 'phaser'
import Config from '../launch/config'
import Matter from 'matter-js'
import Effects from '../effects/effects'
import {isDebug} from '../launch/utils'
import {_addToEventLog} from '../app/app'
import audio from '../launch/audio'

export default class Towable {
    constructor(props) {
        this.constraint = null
        this.id = props.id || Config.chance.guid()
        this.isBeingTowed = props.isBeingTowed || false
        this.isDiscovered = props.isDiscovered || false
        this.isInOrbit = props.isInOrbit || false
        this.isMoving = false
        this.isSelected = false
        this.movingThreshold = 0.02
        this.orbitAngle = 0
        this.orbitSpeed = 0.004
        this.towShipId = props.towShipId || null
        this.type = props.type || 'default'

        this.sprite = Config.scene.matter.add.sprite(
            props.x,
            props.y,
            props.texture || 'star'
        )
        Matter.Body.set(this.sprite.body, {
            density: 0.005, // default 0.001
            // friction: 0, // default 0.1
            frictionAir: 0.005, // default 0.01
            frictionStatic: 8, // default 0.5
            restitution: 0.4, // default 0
            sleepThreshold: 10, // default 60
            isStatic: !this.isBeingTowed && !this.isInOrbit,
        })

        this.sprite.setScale(0.26)
        this.sprite.setDepth(7)
        this.sprite.body[Config.namespace] = {
            isTowableBody: true,
            towableId: this.id,
            type: this.type,
        }

        if (!this.isDiscovered) {
            this.undiscoveredImage = Config.scene.add.image(
                props.x,
                props.y,
                'undiscovered'
            )
            this.undiscoveredImage.setScale(0.25)
            this.undiscoveredImage[Config.namespace] = {
                type: 'towable undiscovered',
            }
        }

        Config.towables[this.id] = this

        if (this.towShipId) {
            const ship = Config.ships[this.towShipId]
            ship && this.addConstraint(ship)
        }

        if (props.orbitingEntityId) {
            this.orbitingEntity = Config.entities[props.orbitingEntityId]
            // Set to false so towable enters orbit as usual
            this.isInOrbit = false
        }
    }

    orbit() {
        if (!this.orbitingEntity) {
            return
        }

        if (!this.isInOrbit && this.orbitingEntity) {
            this.isInOrbit = true

            // Add this sprite to orbit group and create point
            this.orbitGroup = Config.scene.add.group()
            this.orbitGroup.add(this.sprite, false)
            this.orbitPoint = new Phaser.Geom.Point(
                this.orbitingEntity.x,
                this.orbitingEntity.y
            )

            // Stop
            Matter.Body.setVelocity(this.sprite.body, {
                x: 0,
                y: 0,
            })
        }

        // Rotate orbit group children around point
        Phaser.Actions.RotateAroundDistance(
            this.orbitGroup.getChildren(),
            this.orbitPoint,
            this.orbitSpeed,
            Config.orbitDistance
        )

        // Point in direction
        Matter.Body.setAngle(this.sprite.body, (this.orbitAngle -= 0.006))
    }

    update(time, delta) {
        if (!this.sprite.body) {
            return
        }

        this.x = this.sprite.body.position.x
        this.y = this.sprite.body.position.y

        this.isMoving = this.sprite.body.speed > this.movingThreshold

        this.sprite.setVisible(this.isDiscovered)

        if (this.undiscoveredImage) {
            this.undiscoveredImage.alpha = this.isSelected || isDebug ? 1 : 0.7

            if (this.isDiscovered) {
                this.undiscoveredImage.destroy()
                delete this.undiscoveredImage
            }
        }

        this.orbit()
    }

    destroy() {
        Matter.Body.setVelocity(this.sprite.body, {
            x: 0,
            y: 0,
        })

        Config.scene.matter.world.remove(this.sprite)
        this.sprite.destroy()
        if (this.undiscoveredImage) {
            this.undiscoveredImage.destroy()
            delete this.undiscoveredImage
        }

        if (this.orbitingEntity) {
            delete this.orbitingEntity.orbitingTowables[this.id]
        }

        delete Config.towables[this.id]
    }

    deselect() {
        this.isSelected = false
    }

    addConstraint(ship) {
        const towable = this
        const towableId = this.id
        const stiffness = 0.01
        const length = 35

        if (
            towable.isInOrbit ||
            towable.isBeingTowed ||
            ship.faction !== Config.userFactionName ||
            ship.orbitingEntity
        ) {
            return
        }

        if (towable.sprite.body.isStatic) {
            Matter.Body.set(towable.sprite.body, {
                isStatic: false,
            })
            Matter.Body.rotate(towable.sprite.body, 0)
        }

        towable.isBeingTowed = true
        towable.towShipId = ship.id
        ship.towedTowables[towableId] = {
            id: towableId,
        }
        audio.tractorBeam()
        // Add constraint between ship and towable
        towable.constraint = Config.scene.matter.add.constraint(
            ship.sprite.body,
            towable.sprite.body,
            length,
            stiffness,
            {
                damping: 0.1,
                // stiffness,
            }
        )

        // Draw tow lines
        ship.towLines[towableId] = {
            graphics: Config.scene.add.graphics({
                lineStyle: {
                    width: 7,
                    color: `0x7a67e8`,
                    alpha: 0.5,
                },
            }),
            line: new Phaser.Geom.Line(
                ship.x,
                ship.y,
                towable.sprite.body.position.x,
                towable.sprite.body.position.y
            ),
        }

        this.undiscoveredImage && this.undiscoveredImage.destroy()
        this.isDiscovered = true

        // Points!
        new Effects().scoreAnim({
            x: towable.sprite.x,
            y: towable.sprite.y,
            num:
                Config.difficultyLevels[Config.difficulty].shipHitTowablePoints,
            positive: true,
        })

        if (towable.type === 'artifact') {
            _addToEventLog({
                coords: {x: ship.x, y: ship.y},
                html: `The ship <span style="color: #${Config.colorGreen}">${ship.name}</span> has discovered an artifact`,
            })
        }
    }
}
